import { Fragment, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { RefreshCw, Users } from 'lucide-react'
import {
  ServerApiClient,
  type CrmContact,
  type CrmContactDetail,
  type CrmStage,
} from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'

/**
 * The lead desk. What makes it not-a-toy — and not-Salesforce — is the join:
 * every contact arrives already carrying what they did in the product. Open a
 * lead and read the tour steps they viewed, the minutes they spent, and their
 * intent score; nothing here was typed in by a person.
 */

type Props = { topInsetPx?: number; initialContactId?: string | null }

/** One replay event, exactly as `getCrmSessionTimeline` serves it. */
type SessionTimelineEntry = Awaited<ReturnType<ServerApiClient['getCrmSessionTimeline']>>[number]

/**
 * The stage vocabulary's won-state is `customer` — labeled "Won" in the UI.
 * Moving here prompts for first-year value; migration 216's `won_at` stamps
 * on the server when the move lands.
 */
type Stage = CrmStage

const STAGES: Stage[] = ['new', 'working', 'qualified', 'demo_booked', 'customer', 'lost']

const STAGE_LABEL: Record<Stage, string> = {
  new: 'New',
  working: 'Working',
  qualified: 'Qualified',
  demo_booked: 'Demo booked',
  customer: 'Won',
  lost: 'Lost',
}

const STAGE_TINT: Record<Stage, string> = {
  new: '#eef2ff',
  working: '#fef9c3',
  qualified: '#dcfce7',
  demo_booked: '#e0f2fe',
  customer: '#bbf7d0',
  lost: '#f3f4f6',
}

const panel: CSSProperties = {
  border: '1px solid #d9e2df', background: '#fff', borderRadius: 8,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
}
const buttonStyle: CSSProperties = {
  border: '1px solid #c8d7d2', background: '#fff', color: '#15343b', borderRadius: 7,
  padding: '7px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
}
const thStyle: CSSProperties = {
  textAlign: 'left', fontSize: 11, color: '#5d6d72', fontWeight: 800,
  borderBottom: '1px solid #e4ece9', padding: '8px 10px', whiteSpace: 'nowrap',
}
const tdStyle: CSSProperties = {
  fontSize: 12, color: '#223236', borderBottom: '1px solid #eef3f1',
  padding: '8px 10px', verticalAlign: 'top',
}

const fmtDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
const fmtDwell = (ms: number): string => (ms >= 60_000 ? `${(ms / 60_000).toFixed(1)} min` : `${Math.round(ms / 1000)} s`)
const fmtClock = (value: string | null | undefined): string => {
  if (!value) return '--:--:--'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toTimeString().slice(0, 8)
}
const fmtUsd = (cents: number): string => `$${Math.round(cents / 100).toLocaleString('en-US')}`
/** The replay bits worth reading inline: which CTA, which checklist item, how big the DSO gap. */
const timelineExtras = (detail: Record<string, unknown> | null): string[] => {
  if (!detail) return []
  const bits: string[] = []
  if (typeof detail.ctaLabel === 'string' && detail.ctaLabel) bits.push(`cta: ${detail.ctaLabel}`)
  if (typeof detail.checklistItemId === 'string' && detail.checklistItemId) bits.push(`checklist: ${detail.checklistItemId}`)
  if (detail.dsoGapDays != null && Number.isFinite(Number(detail.dsoGapDays))) bits.push(`DSO gap: ${Number(detail.dsoGapDays)}d`)
  return bits
}

export function FounderLeadsDashboard({ topInsetPx = 0, initialContactId = null }: Props) {
  const api = useMemo(() => new ServerApiClient(), [])
  const [contacts, setContacts] = useState<CrmContact[]>([])
  const [stageFilter, setStageFilter] = useState<string | null>(null)
  const [detail, setDetail] = useState<CrmContactDetail | null>(null)
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Session replay: which "What they did" row is open, plus a per-session cache
  // so re-expanding never refetches.
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null)
  const [timelines, setTimelines] = useState<Record<string, SessionTimelineEntry[]>>({})
  const [timelineErrors, setTimelineErrors] = useState<Record<string, string>>({})
  // Marking won asks for the first-year value inline (never window.prompt).
  const [wonPrompt, setWonPrompt] = useState<CrmContact | null>(null)
  const [wonDollars, setWonDollars] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await ensureApiSessionReady()
      setContacts(await api.listCrmContacts(stageFilter))
    } catch (err: unknown) {
      setError(String((err as Error)?.message || 'Failed to load leads.'))
    } finally {
      setLoading(false)
    }
  }, [api, stageFilter])

  useEffect(() => { void load() }, [load])

  const open = useCallback(async (id: string) => {
    try {
      setDetail(await api.getCrmContact(id))
    } catch (err: unknown) {
      setError(String((err as Error)?.message || 'Failed to load the contact.'))
    }
  }, [api])

  // Deep link (?fg_crm_contact=<id>, routed through FounderConsole): land with
  // the contact's detail already open.
  useEffect(() => {
    if (initialContactId) void open(initialContactId)
  }, [initialContactId, open])

  const toggleSession = useCallback(async (sessionId: string) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null)
      return
    }
    setExpandedSessionId(sessionId)
    if (timelines[sessionId]) return
    setTimelineErrors((prev) => (prev[sessionId] ? { ...prev, [sessionId]: '' } : prev))
    try {
      const rows = await api.getCrmSessionTimeline(sessionId)
      setTimelines((prev) => ({ ...prev, [sessionId]: rows }))
    } catch (err: unknown) {
      setTimelineErrors((prev) => ({ ...prev, [sessionId]: String((err as Error)?.message || 'Failed to load the session timeline.') }))
    }
  }, [api, expandedSessionId, timelines])

  const setStage = useCallback(async (contact: CrmContact, stage: Stage) => {
    if (stage === 'customer') {
      // Won carries a first-year value: ask for it inline in the detail pane.
      // Save and Cancel both commit the stage move — won with an unknown value
      // is legal — so nothing is PATCHed until the founder picks one.
      setWonPrompt(contact)
      setWonDollars('')
      void open(contact.id)
      return
    }
    // Optimistic: a rep triaging forty leads must not wait a round-trip per move.
    setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, stage } : c)))
    try {
      await api.updateCrmContact(contact.id, { stage })
    } catch {
      setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, stage: contact.stage } : c)))
      setError('Stage change failed; reverted.')
    }
  }, [api, open])

  const commitWon = useCallback(async (dollars: number | null) => {
    if (!wonPrompt) return
    const target = wonPrompt
    setWonPrompt(null)
    const patch: { stage: CrmStage; won_amount_cents?: number } = { stage: 'customer' }
    if (dollars != null) patch.won_amount_cents = Math.round(dollars * 100)
    try {
      await api.updateCrmContact(target.id, patch)
      setContacts((prev) => prev.map((c) => (c.id === target.id ? { ...c, stage: 'customer' } : c)))
      await open(target.id)
    } catch {
      setError('Marking won failed; the stage is unchanged.')
    }
  }, [api, wonPrompt, open])

  const submitNote = useCallback(async () => {
    if (!detail || !note.trim()) return
    await api.addCrmNote(detail.contact.id, note.trim())
    setNote('')
    await open(detail.contact.id)
  }, [api, detail, note, open])

  return (
    <div style={{ height: `calc(100vh - ${topInsetPx}px)`, marginTop: topInsetPx, overflowY: 'auto', padding: '14px 16px 40px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: '#15343b', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Users size={16} /> Leads
          </h2>
          <span style={{ flex: 1 }} />
          <button
            style={{ ...buttonStyle, background: stageFilter === null ? '#15343b' : '#fff', color: stageFilter === null ? '#fff' : '#15343b' }}
            onClick={() => setStageFilter(null)}
          >All</button>
          {STAGES.map((s) => (
            <button
              key={s}
              style={{ ...buttonStyle, background: stageFilter === s ? '#15343b' : '#fff', color: stageFilter === s ? '#fff' : '#15343b' }}
              onClick={() => setStageFilter(s)}
            >{STAGE_LABEL[s]}</button>
          ))}
          <button style={buttonStyle} onClick={() => void load()} disabled={loading}>
            <RefreshCw size={13} /> {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {error ? (
          <div style={{ ...panel, padding: 12, borderColor: '#e5b4b4', background: '#fdeeee', color: '#8c2f2f', fontSize: 12 }}>{error}</div>
        ) : null}

        <section style={{ ...panel, padding: 12 }}>
          {contacts.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Contact</th>
                  <th style={thStyle}>Stage</th>
                  <th style={thStyle}>Source</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Sessions</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Activity</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Last touch</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} data-testid="crm-lead-row" style={{ cursor: 'pointer' }} onClick={() => void open(c.id)}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700 }}>{c.fullName || c.email}</div>
                      <div style={{ color: '#5d6d72', fontSize: 11 }}>{c.fullName ? c.email : ''}{c.firmName ? ` · ${c.firmName}` : ''}</div>
                    </td>
                    <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                      <select
                        value={c.stage}
                        onChange={(e) => void setStage(c, e.target.value as Stage)}
                        style={{ font: 'inherit', fontSize: 12, fontWeight: 700, padding: '3px 6px', borderRadius: 6, border: '1px solid #d9e2df', background: STAGE_TINT[c.stage] }}
                        aria-label={`Stage for ${c.email}`}
                      >
                        {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                      </select>
                    </td>
                    <td style={tdStyle}>{c.source}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{c.sessionIds.length}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{c.activityCount}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtDate(c.lastActivityAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 12, color: '#5d6d72' }}>
              No leads{stageFilter ? ` in ${STAGE_LABEL[stageFilter as Stage] || stageFilter}` : ''} yet. Every tour lead-step
              submission and demo-gate unlock lands here automatically.
            </div>
          )}
        </section>

        {detail ? (
          <section style={{ ...panel, padding: 14 }} data-testid="crm-detail">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#15343b' }}>
                {detail.contact.fullName || detail.contact.email}
              </h3>
              <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: STAGE_TINT[detail.contact.stage] || '#f3f4f6', color: '#15343b' }}>
                {STAGE_LABEL[detail.contact.stage] || detail.contact.stage}
              </span>
              {detail.contact.wonAmountCents != null || detail.contact.wonAt ? (
                <span style={{ fontSize: 12, fontWeight: 800, color: '#166534' }} data-testid="crm-won-summary">
                  {detail.contact.wonAmountCents != null ? fmtUsd(detail.contact.wonAmountCents) : 'value unknown'}
                  {detail.contact.wonAt ? ` · won ${fmtDate(detail.contact.wonAt)}` : ''}
                </span>
              ) : null}
              <span style={{ fontSize: 12, color: '#5d6d72' }}>{detail.contact.email}{detail.contact.firmName ? ` · ${detail.contact.firmName}` : ''}{detail.contact.phone ? ` · ${detail.contact.phone}` : ''}</span>
              <span style={{ flex: 1 }} />
              <button style={buttonStyle} onClick={() => setDetail(null)}>Close</button>
            </div>

            {wonPrompt && wonPrompt.id === detail.contact.id ? (
              <div data-testid="crm-won-prompt" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: 10, border: '1px solid #b7e2c3', background: '#f0fdf4', borderRadius: 7 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#15343b' }}>First-year value ($)</span>
                <input
                  value={wonDollars}
                  onChange={(e) => setWonDollars(e.target.value)}
                  inputMode="decimal"
                  placeholder="12000"
                  aria-label="First-year value in dollars"
                  data-testid="crm-won-input"
                  style={{ width: 110, font: 'inherit', fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid #d9e2df' }}
                />
                <button
                  style={buttonStyle}
                  disabled={!wonDollars.trim() || !Number.isFinite(Number(wonDollars))}
                  onClick={() => void commitWon(Number(wonDollars))}
                >Save</button>
                <button style={buttonStyle} onClick={() => void commitWon(null)}>Cancel</button>
                <span style={{ fontSize: 11, color: '#5d6d72' }}>Cancel still marks the lead won — just without a value.</span>
              </div>
            ) : null}

            {detail.tourSessions.length ? (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#5d6d72', textTransform: 'uppercase', letterSpacing: '0.04em' }}>What they did</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
                  <tbody>
                    {detail.tourSessions.map((s) => (
                      <Fragment key={s.sessionId}>
                        <tr data-testid="crm-session-row" style={{ cursor: 'pointer' }} onClick={() => void toggleSession(s.sessionId)}>
                          <td style={tdStyle}>{fmtDate(s.startedAt)}</td>
                          <td style={tdStyle}>{s.tourSlug}</td>
                          <td style={tdStyle}>reached <b>{s.lastStepId || '-'}</b> ({s.stepsViewed}{s.stepCount ? `/${s.stepCount}` : ''} steps)</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtDwell(s.dwellMs)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>intent {s.intent.toFixed(0)}</td>
                          <td style={tdStyle}>{[s.finished ? 'finished' : null, s.identified ? 'lead' : null].filter(Boolean).join(' · ')}</td>
                        </tr>
                        {expandedSessionId === s.sessionId ? (
                          <tr data-testid="crm-session-timeline">
                            <td colSpan={6} style={{ ...tdStyle, background: '#f7faf9', padding: '4px 10px 10px' }}>
                              {timelineErrors[s.sessionId] ? (
                                <div style={{ fontSize: 11, color: '#8c2f2f' }}>{timelineErrors[s.sessionId]}</div>
                              ) : !timelines[s.sessionId] ? (
                                <div style={{ fontSize: 11, color: '#5d6d72' }}>Loading replay…</div>
                              ) : timelines[s.sessionId].length ? (
                                <ol style={{ margin: 0, padding: '0 0 0 16px', display: 'grid', gap: 2 }}>
                                  {timelines[s.sessionId].map((e, i) => {
                                    const extras = timelineExtras(e.detail)
                                    return (
                                      <li key={i} style={{ fontSize: 11, color: '#223236' }}>
                                        <span style={{ color: '#5d6d72', fontVariantNumeric: 'tabular-nums' }}>{fmtClock(e.at)}</span>
                                        {' '}<b>{e.event.replace(/^tour\./, '')}</b>
                                        {e.stepId ? ` · ${e.stepId}` : ''}
                                        {e.dwellMs != null && e.dwellMs > 0 ? ` · ${Math.round(e.dwellMs / 1000)}s` : ''}
                                        {extras.length ? ` · ${extras.join(' · ')}` : ''}
                                      </li>
                                    )
                                  })}
                                </ol>
                              ) : (
                                <div style={{ fontSize: 11, color: '#5d6d72' }}>No events recorded for this session.</div>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ fontSize: 12, color: '#5d6d72', margin: '8px 0 0' }}>No tour sessions recorded for this contact.</p>
            )}

            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#5d6d72', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Timeline</div>
              <div style={{ display: 'grid', gap: 4, marginTop: 4 }}>
                {detail.activities.map((a) => (
                  <div key={a.id} style={{ fontSize: 12, color: '#223236', display: 'flex', gap: 8 }}>
                    <span style={{ color: '#5d6d72', whiteSpace: 'nowrap' }}>{fmtDate(a.createdAt)}</span>
                    <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{a.kind}</span>
                    <span>{a.body}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void submitNote() }}
                placeholder="Add a note — Enter to save"
                aria-label="Add a note"
                style={{ flex: 1, font: 'inherit', fontSize: 13, padding: '8px 10px', borderRadius: 7, border: '1px solid #d9e2df' }}
              />
              <button style={buttonStyle} onClick={() => void submitNote()}>Save note</button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  )
}
